import { CreatorGradeCode, CreatorGradeSource, resolveCreatorGradeCode } from '@repo/services/hooks';
import { getServiceName } from '@repo/utils';

import { CreatorBadgeIcon, CreatorBadgeIconVariant } from '../../Icons';

/**
 * 크리에이터 등급 뱃지 (#2039).
 * 유저 응답을 그대로 받아 등급 코드를 판정하고, NONE(일반 유저)이면 아무것도 그리지 않는다.
 * 판정은 `resolveCreatorGradeCode` 한 곳에서만 하므로 서버에 등급이 추가돼도 호출부는 손대지 않는다.
 */
const BADGE_VARIANT_BY_GRADE: Record<Exclude<CreatorGradeCode, 'NONE'>, CreatorBadgeIconVariant> = {
  STARDUST: CreatorBadgeIconVariant.STARDUST,
  RISING_STAR: CreatorBadgeIconVariant.RISING_STAR,
  STARKEEPER: CreatorBadgeIconVariant.STARKEEPER,
};

interface ICreatorGradeBadgeProps {
  /**
   * 등급 판정 1순위. 등급 코드가 실린 응답을 넘긴다 — 애셋의 작성자 객체(`CompactAiUserDto`)나
   * 본인 프로필(`/my/profile`).
   */
  user?: CreatorGradeSource | null;
  /**
   * 등급 판정 2순위. `GET /users/{id}` 의 작가 프로필(`PublicUserDto`)처럼 등급 필드가 없는 응답.
   * 1순위에 등급이 없을 때만 isCreator 폴백(별가루)에 쓰인다.
   */
  fallbackUser?: CreatorGradeSource | null;
  size?: number;
  className?: string;
}

function CreatorGradeBadge({ user, fallbackUser, size = 20, className }: ICreatorGradeBadgeProps) {
  // 팅글은 등급제 미적용 — 아트도 노출 판정도 4.56.0 이전과 같게 둔다.
  // 판정까지 `resolveCreatorGradeCode` 에 맡기면, 등급을 쓰지 않는 팅글 서버가 크리에이터에게
  // `creatorGradeCode: 'NONE'`(또는 팅글 전용 코드)을 내려주는 순간 팅글 전 지면에서 뱃지가
  // 통째로 사라진다. 4.56.0 이전 `ChatProfilePage` 가 `!!userData?.isCreator` 만 봤던 것과 맞춘다.
  if (getServiceName() === 'tingle') {
    if (!resolveIsCreator(user, fallbackUser)) return null;

    return <CreatorBadgeIcon className={className} size={size} variant={CreatorBadgeIconVariant.LEGACY_WINGED} />;
  }

  const gradeCode = resolveCreatorGradeCode(user, fallbackUser);
  if (gradeCode === 'NONE') return null;

  return <CreatorBadgeIcon className={className} size={size} variant={BADGE_VARIANT_BY_GRADE[gradeCode]} />;
}

/**
 * 팅글 전용 노출 판정.
 *
 * `resolveCreatorGradeCode` 와 **필드 우선순위가 반대**다. 그쪽은 필드가 먼저라 전 소스에서
 * `creatorGradeCode` 를 훑고 없을 때만 `isCreator` 를 보지만, 여기는 `isCreator` 가 먼저고
 * 그 필드가 아예 없는 응답만 등급 코드로 폴백한다. 팅글은 등급제를 안 쓰므로 등급 코드에
 * 판정을 맡기면 서버가 NONE 을 주는 순간 뱃지가 사라지기 때문이다.
 *
 * **소스 순서도 반대다 — 작가 프로필(`fallbackUser`)이 1순위.** 4.56.0 이전 챗 4개 지면이
 * `writerProfile?.isCreator` 만 봤고, 애셋의 작성자 객체는 애셋 행에 비정규화돼 복사된 값이라
 * 프로필보다 오래됐을 수 있다. 다만 프로필 조회 전(`undefined`)에는 작성자 객체로 먼저 그려
 * 첫 페인트에 뱃지가 늦게 튀어나오지 않게 한다.
 */
function resolveIsCreator(user?: CreatorGradeSource | null, fallbackUser?: CreatorGradeSource | null) {
  const isCreator = fallbackUser?.isCreator ?? user?.isCreator;
  if (isCreator != null) return isCreator;

  return resolveCreatorGradeCode(user, fallbackUser) !== 'NONE';
}

export default CreatorGradeBadge;
